import { computed, isRef, ref, type Ref, type ComputedRef } from 'vue';

export type RefParam<T> = Readonly<Ref<T>> | ComputedRef<T> | ((this: void) => T);

export function toRef<T, K extends keyof T>(obj: RefParam<T>, key: K): Ref<T[K]>;
export function toRef<T, K extends keyof T>(obj: T, key: K): Ref<T[K]>;
export function toRef<T>(param: RefParam<T>): ComputedRef<T>;
export function toRef<T>(param: RefParam<T> | undefined): ComputedRef<T | undefined>;
export function toRef<T>(param: T): ComputedRef<T>;
export function toRef<T, K extends keyof T>(
    param: RefParam<T> | T,
    key?: K
): Readonly<Ref<T>> | Ref<T[K]> {
    const wrapped = wrapWithRef(param);

    if (key == null) {
        return wrapped;
    }

    return computed({
        get: () => wrapped.value[key],
        set: (v) => (wrapped.value[key] = v),
    });
}

function wrapWithRef<T>(param: RefParam<T> | T) {
    if (isRef(param)) {
        return param;
    }

    if (param instanceof Function || typeof param === 'function') {
        return computed(param as (this: void) => T);
    }

    return ref(param) as Readonly<Ref<T>>;
}
